import { ITourStep, TourModule } from "training/types";

/**
 * CLE-3015 (John's review) — the tour runs one module at a time. Pure helpers for the engine's module
 * boundaries, unit-tested without driver.js.
 *
 * A step belongs to the stretch of the tour it is counted in: `countIn`, else `module` (4.10 Direct Pay is a
 * Module 4 step run inside Module 6, so it belongs to Module 6's stretch). Within one run, modules come in
 * stretches of consecutive visible steps.
 */
export const tourModuleOf = (step: ITourStep): TourModule =>
  step.countIn ?? step.module;

/**
 * The step at `index` opens its module in this run: it is the first visible step, or the step before it
 * belongs to another module. That is where the module's title screen shows.
 */
export function opensModule(visible: ITourStep[], index: number): boolean {
  const step = visible[index];
  if (!step) return false;
  const previous = visible[index - 1];
  return !previous || tourModuleOf(previous) !== tourModuleOf(step);
}

/**
 * The run of steps one driver instance drives around `index`: consecutive steps on the same route AND in the
 * same module. Ending a segment at a module boundary means driver.js never moves (or skips) from one module
 * into the next by itself; the engine always decides there, so a module's quiz cannot be stepped over.
 */
export function moduleSegmentAround(
  visible: ITourStep[],
  index: number
): { start: number; end: number } {
  const { route } = visible[index];
  const module = tourModuleOf(visible[index]);
  const same = (step: ITourStep) =>
    step.route === route && tourModuleOf(step) === module;
  let start = index;
  while (start > 0 && same(visible[start - 1])) start -= 1;
  let end = index;
  while (end < visible.length - 1 && same(visible[end + 1])) end += 1;
  return { start, end };
}
